import React, { useEffect, useMemo, useState } from 'react';
import CaffeineFields, { caffeineDefaults } from './CaffeineFields';

const DEFAULT_PFAND_VALUE = '0,25 €';
const ZERO_PFAND_VALUE = '0,00 €';
const QUICK_PFAND_VALUES = [
  { value: '0,08 €', label: '0,08 €' },
  { value: '0,15 €', label: '0,15 €' },
  { value: '0,25 €', label: '0,25 €' },
  { value: ZERO_PFAND_VALUE, label: '0,00 € alkoholisch', forceStatus: 'alkoholisch' },
];

export default function UnknownDrinkSubmissionForm({
  defaultEan = '',
  apiUrl = window?.pfandpiratData?.drinkSubmissionApiUrl || '/wp-json/pfandpirat/v1/drink-submissions',
  onCancel,
  onSuccess,
}) {
  const categoriesUrl = useMemo(() => {
    try {
      const u = new URL(window?.pfandpiratData?.drinksApiUrl || '/wp-json/pfandpirat/v1/drinks', window.location.origin);
      return `${u.origin}/wp-json/pfandpirat/v1/drink-categories`;
    } catch {
      return '/wp-json/pfandpirat/v1/drink-categories';
    }
  }, []);

  const [form, setForm] = useState({
    ...caffeineDefaults,
    product_name: '',
    ean_code: defaultEan,
    volume: '',
    pfand_info: DEFAULT_PFAND_VALUE,
    alcohol_content: '',
    status: 'nicht alkoholisch',
    category_id: '',
    description: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    setForm((prev) => ({ ...prev, ean_code: defaultEan }));
  }, [defaultEan]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(categoriesUrl, { credentials: 'include' });
        if (!res.ok) throw new Error('Kategorien konnten nicht geladen werden.');
        const data = await res.json();
        if (active) setCategories(Array.isArray(data) ? data : []);
      } catch {
        if (active) setCategories([]);
      }
    })();
    return () => { active = false; };
  }, [categoriesUrl]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'status' && value !== 'alkoholisch' && prev.pfand_info === ZERO_PFAND_VALUE) {
        next.pfand_info = DEFAULT_PFAND_VALUE;
      }
      return next;
    });
  };

  const pickPfand = (value) => {
    setForm((prev) => ({
      ...prev,
      pfand_info: value,
      status: value === ZERO_PFAND_VALUE ? 'alkoholisch' : prev.status,
    }));
  };

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);
    setError('');

    try {
      const payload = {
        ...form,
        volume: form.volume !== '' ? parseFloat(String(form.volume).replace(',', '.')) : null,
        alcohol_content: form.alcohol_content !== '' ? parseFloat(String(form.alcohol_content).replace(',', '.')) : null,
        category_id: form.category_id !== '' ? parseInt(form.category_id, 10) : null,
        source: 'scan',
      };

      const res = await fetch(apiUrl, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': window?.pfandpiratData?.nonce || '',
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || `Fehler ${res.status}`);
      }

      onSuccess?.(data);
    } catch (submitError) {
      console.error(submitError);
      setError(submitError.message || 'Vorschlag konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="unknown-drink-card">
      <h3 className="unknown-drink-title">Getränk vorschlagen</h3>
      <p className="unknown-drink-hint">
        Dieser Barcode ist noch nicht in der Datenbank. Dein Vorschlag wird als Entwurf gespeichert und erst nach Admin-Prüfung freigegeben.
      </p>

      <label className="unknown-drink-label">
        EAN-Code
        <input name="ean_code" value={form.ean_code} readOnly className="unknown-drink-input unknown-drink-input--readonly" />
      </label>

      <label className="unknown-drink-label">
        Produktname (optional)
        <input name="product_name" value={form.product_name} onChange={handleChange} className="unknown-drink-input" />
      </label>

      <label className="unknown-drink-label">
        Kategorie (optional)
        <select name="category_id" value={form.category_id} onChange={handleChange} className="unknown-drink-input">
          <option value="">— Bitte wählen —</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>{category.name}</option>
          ))}
        </select>
      </label>

      <label className="unknown-drink-label">
        Volumen in Litern (optional)
        <input name="volume" value={form.volume} onChange={handleChange} placeholder="z. B. 0,5" inputMode="decimal" className="unknown-drink-input" />
      </label>

      <div className="unknown-drink-label">
        Pfand
        <div className="unknown-drink-pfand-row">
          {QUICK_PFAND_VALUES.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`unknown-drink-pfand-btn ${form.pfand_info === option.value ? 'is-active' : ''}`}
              onClick={() => pickPfand(option.value)}
              title={option.forceStatus ? 'Setzt den Status automatisch auf alkoholisch.' : ''}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className="unknown-drink-meta">
          <div><strong>0,08 €</strong> meist Mehrweg-Glasflaschen</div>
          <div><strong>0,15 €</strong> oft Mehrweg-PET</div>
          <div><strong>0,25 €</strong> oft Einwegflaschen und Dosen</div>
          <div><strong>0,00 €</strong> nur für alkoholische Getränke ohne Pfand</div>
        </div>
      </div>

      <label className="unknown-drink-label">
        Alkoholgehalt % (optional)
        <input name="alcohol_content" value={form.alcohol_content} onChange={handleChange} inputMode="decimal" className="unknown-drink-input" />
      </label>

      <label className="unknown-drink-label">
        Status
        <select name="status" value={form.status} onChange={handleChange} className="unknown-drink-input">
          <option value="nicht alkoholisch">Nicht alkoholisch</option>
          <option value="alkoholisch">Alkoholisch</option>
        </select>
      </label>

      <label className="unknown-drink-label">
        Beschreibung (optional)
        <textarea name="description" value={form.description} onChange={handleChange} rows="3" className="unknown-drink-input" />
      </label>

      <CaffeineFields form={form} setForm={setForm} />
      {error ? <p role="alert" className="unknown-drink-error">{error}</p> : null}

      <div className="unknown-drink-actions">
        <button type="submit" className="pfand-button" disabled={saving}>{saving ? 'Speichert…' : 'Vorschlag absenden'}</button>
        <button type="button" className="pfand-button unknown-drink-cancel" onClick={onCancel}>Abbrechen</button>
      </div>
    </form>
  );
}
